import { useCallback, useEffect, useState } from "react";
import { listFactors, getMyMfaStatus } from "@/lib/mfa";
import { MFA_ENABLED, computeMfaRequiredAfter, EMERGENCY_BYPASS_EMAILS } from "@/config/mfaFlags";
import { useAuth } from "@/context/Auth";

export type MfaStatus = {
  loading: boolean;
  enrolled: boolean;
  lastVerifiedAt: string | null;
  /** Authoritative deadline from mfa_get_my_status RPC; falls back to computed value. */
  requiredAfter: Date | null;
  refresh: () => Promise<void>;
};

/**
 * Lightweight client-side hook for MFA enrollment + grace deadline.
 * Fails open (treats as "not enrolled", no deadline) on any error.
 */
export function useMfaStatus(): MfaStatus {
  const { session, roles, tier } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [lastVerifiedAt, setLastVerifiedAt] = useState<string | null>(null);
  const [requiredAfter, setRequiredAfter] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!session?.user || !MFA_ENABLED) {
      setEnrolled(false);
      setRequiredAfter(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [{ verified }, status] = await Promise.all([listFactors(), getMyMfaStatus()]);
      setEnrolled(verified.length > 0);
      setLastVerifiedAt(status?.last_verified_at ?? null);

      const email = (session.user.email ?? "").toLowerCase();
      if (email && EMERGENCY_BYPASS_EMAILS.includes(email)) {
        setRequiredAfter(null);
      } else if (status?.mfa_required_after) {
        // Authoritative server value
        setRequiredAfter(new Date(status.mfa_required_after));
      } else {
        // Frontend fallback until gate persists the deadline
        const createdAt = session.user.created_at ? new Date(session.user.created_at) : null;
        setRequiredAfter(
          computeMfaRequiredAfter({ email, roles, tier, accountCreatedAt: createdAt }),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id, roles.join("|"), tier]);

  useEffect(() => {
    load();
  }, [load]);

  return { loading, enrolled, lastVerifiedAt, requiredAfter, refresh: load };
}
